"use client";

/** A small red ring on a diagram ring's track, in that diagram's units. */
export interface OrbitDot {
  x: number;
  y: number;
  r: number;
}

interface RingOrbitProps {
  /** Centre of the track the dots sit on, in the enclosing SVG's user units. */
  cx: number;
  cy: number;
  dots: OrbitDot[];
  /** Figma draws the dots at 2px, and at 1.5px on the smaller Core Services rings. */
  strokeWidth?: number;
}

/**
 * The small red rings that ride a diagram ring's track and circle it for as
 * long as the page is open. The designer's brief: the outermost ring turns red
 * on the selected item (each page does that itself), and the small red rings
 * inside "xoay tròn mặc định luôn", meaning they always turn, selected or not.
 *
 * Shared by Compliance Overview, Standard Delivers and Core Services so all
 * three turn at one speed, in one direction, and stop the same way under
 * reduced motion.
 *
 * Render it inside the SVG group that already places the ring, with `cx`/`cy`
 * in that group's coordinates. `transform-box: view-box` makes the origin a
 * point in those user units, not a share of the dots' own bounding box: three
 * dots spread around a circle have a bounding box whose centre is not the
 * circle's.
 */
export default function RingOrbit({ cx, cy, dots, strokeWidth = 2 }: RingOrbitProps) {
  return (
    <g className="ring-orbit" style={{ transformOrigin: `${cx}px ${cy}px` }}>
      {dots.map((dot) => (
        <circle
          key={`${dot.x},${dot.y}`}
          cx={dot.x}
          cy={dot.y}
          r={dot.r}
          fill="none"
          stroke="var(--color-primary)"
          strokeWidth={strokeWidth}
        />
      ))}

      <style jsx>{`
        /* 14s a turn: slow enough to read as ambient, not as a loader. */
        .ring-orbit {
          transform-box: view-box;
          animation: ringOrbit 14s linear infinite;
        }
        @keyframes ringOrbit {
          to {
            transform: rotate(360deg);
          }
        }
        /* At rest the dots sit exactly where Figma draws them. */
        @media (prefers-reduced-motion: reduce) {
          .ring-orbit {
            animation: none;
            transform: none;
          }
        }
      `}</style>
    </g>
  );
}
